import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import MathExpressionNew from '../common/mathjax';
import './lesson-preview.scss';

export interface LessonStep {
  step_number: number;
  title?: string;
  teacher_content?: string;
  chart_url?: string;
  quiz?: {
    question?: string;
    options?: {
      a?: string;
      b?: string;
      c?: string;
    };
    correct_option?: string;
  };
  teacher_summary?: string;
}

interface LessonPreviewUIProps {
  steps: LessonStep[];
  topicId?: number;
  topicName?: string;
  subTopicName?: string;
  levelName?: string;
  previewFilter?: { stepNum: number; section: 'teacher_content' | 'quiz_question' | 'teacher_summary' } | null;
}

export const LessonPreviewUI: React.FC<LessonPreviewUIProps> = ({
  steps,
  previewFilter,
  topicName,
  subTopicName,
  levelName
}) => {
  const { t } = useTranslation();

  if (!steps || steps.length === 0) {
    return <Typography>{t('noStepsDefined')}</Typography>;
  }

  const effectiveFilter = previewFilter || null;
  const printDateString = new Date().toLocaleDateString();

  return (
    <div className="lesson-preview-wrapper">
      {/* Print-only branded header */}
      <div className="preview-print-header">
        <div className="print-header-top">
          <span className="brand-title">{t('lessonPlanPrintHeader')}</span>
          <span className="print-date">
            {t('printDate')}: {printDateString}
          </span>
        </div>
        <div className="print-header-meta">
          {topicName && (
            <div className="meta-item">
              <span className="meta-label">{t('topic')}:</span>
              <span className="meta-val">{topicName}</span>
            </div>
          )}
          {subTopicName && (
            <div className="meta-item">
              <span className="meta-label">{t('subTopic')}:</span>
              <span className="meta-val">{subTopicName}</span>
            </div>
          )}
          {levelName && (
            <div className="meta-item">
              <span className="meta-label">{t('level')}:</span>
              <span className="meta-val">{levelName}</span>
            </div>
          )}
        </div>
      </div>

      {steps
        .filter((step) => effectiveFilter === null || step.step_number === effectiveFilter.stepNum)
        .map((step) => (
          <Box key={step.step_number} className="preview-step-container">
            <Typography variant="h6" className="preview-step-title">
              {t('STEP')} {step.step_number} {step.title ? `- ${step.title}` : ''}
            </Typography>

            {(effectiveFilter === null || effectiveFilter.section === 'teacher_content') && (
              <Box className="preview-section-box light-blue">
                <Typography variant="subtitle2" className="preview-section-subtitle">{t('teacherPartTitle')}</Typography>
                <div className="preview-section-body">
                  <MathExpressionNew content={step.teacher_content || ''} />
                </div>
                {step.chart_url && (
                  <div className="preview-chart-container">
                    <img src={step.chart_url} alt={t('generatedChartAlt')} className="generated-chart-img" />
                  </div>
                )}
              </Box>
            )}

            {(effectiveFilter === null || effectiveFilter.section === 'quiz_question') && (
              <Box className="preview-section-box light-green">
                <Typography variant="subtitle2" className="preview-section-subtitle">{t('checkQuestionTitle')}</Typography>
                <div className="preview-section-body">
                  <MathExpressionNew content={step.quiz?.question || t('missingQuestion')} />
                </div>
                {step.quiz?.options && [{key: 'a', label: t('optionA')}, {key: 'b', label: t('optionB')}, {key: 'c', label: t('optionC')}].map(({ key, label }) => {
                  const isCorrect = step.quiz!.correct_option === key;
                  const value = (step.quiz!.options as any)[key];
                  return (
                    <div key={key} className={`preview-quiz-option ${isCorrect ? 'correct' : ''}`}>
                      <span className="option-label">{label}.</span>
                      <MathExpressionNew content={value} />
                      {isCorrect && <span className="correct-mark">✓</span>}
                    </div>
                  );
                })}
              </Box>
            )}

            {(effectiveFilter === null || effectiveFilter.section === 'teacher_summary') && (
              <Box className="preview-section-box light-purple">
                <Typography variant="subtitle2" className="preview-section-subtitle">{t('stepSummaryTitle')}</Typography>
                <div className="preview-section-body">
                  <MathExpressionNew content={step.teacher_summary || t('missingContent')} />
                </div>
              </Box>
            )}
          </Box>
        ))}
    </div>
  );
};
